import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';

import { SUPERVISOR_REVIEW_TAB_OPTIONS } from '../../../../constants';
import {
  selectAnnotatorOneAllConceptsExpanded,
  selectAnnotatorOneExpandedConceptIds,
  selectAnnotatorTwoAllConceptsExpanded,
  selectAnnotatorTwoExpandedConceptIds,
  selectReviewTabSelected,
} from '../../../../state/selector';
import {
  addToAnnotatorOneExpandedConceptIds,
  addToAnnotatorTwoExpandedConceptIds,
  removeFromAnnotatorOneExpandedConceptIds,
  removeFromAnnotatorTwoExpandedConceptIds,
  clearAnnotatorOneAllConceptsExpanded,
  clearAnnotatorTwoAllConceptsExpanded,
} from '../../../../state/supervisorSlice';

interface UseAnnotatorConceptCardProps {
  conceptId: string;
}

interface UseAnnotatorConceptCardResults {
  isExpanded: boolean;
  handleSetIsExpanded: (isExpanded: boolean) => void;
}

export const useAnnotatorConceptCard = ({
  conceptId,
}: UseAnnotatorConceptCardProps): UseAnnotatorConceptCardResults => {
  const dispatch = useAppDispatch();
  const selectedReviewTab = useAppSelector(selectReviewTabSelected);

  // Read both annotators' state unconditionally so hooks run in a stable order,
  // then pick the slice for the selected tab.
  const annotatorOneAllConceptsExpanded = useAppSelector(selectAnnotatorOneAllConceptsExpanded);
  const annotatorTwoAllConceptsExpanded = useAppSelector(selectAnnotatorTwoAllConceptsExpanded);
  const annotatorOneExpandedConceptIds = useAppSelector(selectAnnotatorOneExpandedConceptIds);
  const annotatorTwoExpandedConceptIds = useAppSelector(selectAnnotatorTwoExpandedConceptIds);

  const isAnnotatorOneTab = selectedReviewTab === SUPERVISOR_REVIEW_TAB_OPTIONS[0];
  const areAllConceptsExpanded = isAnnotatorOneTab
    ? annotatorOneAllConceptsExpanded
    : annotatorTwoAllConceptsExpanded;
  const expandedConceptIds = isAnnotatorOneTab
    ? annotatorOneExpandedConceptIds
    : annotatorTwoExpandedConceptIds;

  const isExpanded = expandedConceptIds.includes(conceptId);
  const conceptIdString = conceptId;

  const handleSetIsExpanded = (isExpanded: boolean) => {
    if (selectedReviewTab === SUPERVISOR_REVIEW_TAB_OPTIONS[0]) {
      if (isExpanded) {
        dispatch(addToAnnotatorOneExpandedConceptIds(conceptIdString));
      } else {
        dispatch(removeFromAnnotatorOneExpandedConceptIds(conceptIdString));
      }
      // When user manually toggles, clear the "all expanded" state since it's no longer synchronized
      dispatch(clearAnnotatorOneAllConceptsExpanded());
    } else if (selectedReviewTab === SUPERVISOR_REVIEW_TAB_OPTIONS[1]) {
      if (isExpanded) {
        dispatch(addToAnnotatorTwoExpandedConceptIds(conceptIdString));
      } else {
        dispatch(removeFromAnnotatorTwoExpandedConceptIds(conceptIdString));
      }
      // When user manually toggles, clear the "all expanded" state since it's no longer synchronized
      dispatch(clearAnnotatorTwoAllConceptsExpanded());
    }
  };

  useEffect(() => {
    // Only sync when areAllConceptsExpanded is explicitly true or false, not undefined
    if (selectedReviewTab === SUPERVISOR_REVIEW_TAB_OPTIONS[0]) {
      if (areAllConceptsExpanded === true) {
        dispatch(addToAnnotatorOneExpandedConceptIds(conceptIdString));
      } else if (areAllConceptsExpanded === false) {
        dispatch(removeFromAnnotatorOneExpandedConceptIds(conceptIdString));
      }
    } else if (selectedReviewTab === SUPERVISOR_REVIEW_TAB_OPTIONS[1]) {
      if (areAllConceptsExpanded === true) {
        dispatch(addToAnnotatorTwoExpandedConceptIds(conceptIdString));
      } else if (areAllConceptsExpanded === false) {
        dispatch(removeFromAnnotatorTwoExpandedConceptIds(conceptIdString));
      }
    }
    // If undefined, do nothing - let individual card state persist
  }, [areAllConceptsExpanded, conceptIdString, dispatch, selectedReviewTab]);

  return {
    isExpanded,
    handleSetIsExpanded,
  };
};
